import { useEffect, useRef, useState } from "react";
import { usePageAlive } from "@/hooks/usePageAlive";
import { useTheme } from "@/hooks/useTheme";
import {
  PreviewChartAdapter,
  type StreamAccumulator,
} from "@/lib/preview-stream";

type Props = {
  /** When false the panel stays mounted but skips heavy work. */
  active?: boolean;
  accumulator: StreamAccumulator | null;
  /** Bumps when the accumulator ingests new events (throttled). */
  version: number;
  streamStatus?: string;
  barCount?: number;
  className?: string;
  /** Draw candles and volume only: no trades, plots or drawings. */
  candlesOnly?: boolean;
  /** The chart shows the newest candles while older history is still loading. */
  historyLoading?: boolean;
  /** Candles of the full history received so far. */
  historyBarCount?: number;
};

const loaded = (acc: StreamAccumulator | null) =>
  !!acc && (acc.phase === "live" || acc.phase === "ended" || acc.phase === "failed");

function ChartSkeleton() {
  return (
    <div
      className="absolute inset-0 flex flex-col gap-2 p-4"
      aria-hidden
    >
      <div className="flex h-6 items-center gap-2">
        <div className="h-3 w-16 animate-pulse rounded bg-[var(--border)]" />
        <div className="h-3 w-10 animate-pulse rounded bg-[var(--border)]" />
        <div className="h-3 w-24 animate-pulse rounded bg-[var(--border)]" />
      </div>
      <div className="relative min-h-0 flex-1 overflow-hidden rounded border border-[var(--divider)] bg-[var(--bg-card-elevated)]/40">
        <div className="absolute inset-x-0 bottom-[18%] top-8 flex items-end justify-around gap-0.5 px-3 opacity-60">
          {Array.from({ length: 28 }, (_, i) => {
            const h = 20 + ((i * 37) % 55);
            const up = i % 3 !== 0;
            return (
              <div
                key={i}
                className="w-[3%] max-w-[10px] animate-pulse rounded-sm"
                style={{
                  height: `${h}%`,
                  background: up
                    ? "color-mix(in srgb, var(--trading-profit) 35%, transparent)"
                    : "color-mix(in srgb, var(--trading-loss) 35%, transparent)",
                  animationDelay: `${(i % 7) * 60}ms`,
                }}
              />
            );
          })}
        </div>
        <div className="absolute inset-x-3 bottom-3 h-8 animate-pulse rounded bg-[var(--border)]/50" />
      </div>
    </div>
  );
}

/**
 * TradingView Lightweight Charts panel fed by a preview_stream accumulator.
 * Tab switches stay responsive: shell + skeleton paint first; the engine and
 * bulk setData are deferred to the next frames.
 */
export function BacktestChart({
  active = true,
  accumulator,
  version,
  streamStatus = "idle",
  barCount = 0,
  className = "min-h-0 w-full flex-1",
  candlesOnly = false,
  historyLoading = false,
  historyBarCount = 0,
}: Props) {
  const { theme } = useTheme();
  const pageAlive = usePageAlive();
  const hostRef = useRef<HTMLDivElement>(null);
  const adapterRef = useRef<PreviewChartAdapter | null>(null);
  const [engineAllowed, setEngineAllowed] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const [painting, setPainting] = useState(false);
  const accIdRef = useRef<StreamAccumulator | null>(null);
  const chartReadyRef = useRef(false);
  const pendingApplyRef = useRef(0);
  const pendingResetRef = useRef(false);

  // Let the tab chrome paint before we touch Lightweight Charts.
  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    const outer = requestAnimationFrame(() => {
      const inner = requestAnimationFrame(() => {
        if (!cancelled) setEngineAllowed(true);
      });
      pendingApplyRef.current = inner;
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(outer);
      cancelAnimationFrame(pendingApplyRef.current);
    };
  }, [active]);

  // The chart lives only while this panel is mounted and the page is open:
  // leaving the route or reloading removes it and frees its series data.
  useEffect(() => {
    if (!engineAllowed || !pageAlive) return;
    const el = hostRef.current;
    if (!el) return;

    const dark = theme === "dark";
    const adapter = new PreviewChartAdapter();
    adapter.mount(
      el,
      {
        background: "transparent",
        text: dark ? "#A1A1AA" : "#52525B",
        grid: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)",
        border: dark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.12)",
      },
      { candlesOnly }
    );
    adapterRef.current = adapter;
    setChartReady(false);
    chartReadyRef.current = false;
    if (pendingResetRef.current) {
      pendingResetRef.current = false;
      adapter.resetForNewStream();
    }

    return () => {
      adapter.destroy();
      adapterRef.current = null;
      chartReadyRef.current = false;
    };
  }, [engineAllowed, theme, candlesOnly, pageAlive]);

  // New accumulator → reset only when the tab is visible (avoids freezing Run
  // when Overview is shown and the old candle buffer is discarded).
  useEffect(() => {
    if (accumulator !== accIdRef.current) {
      const hadChart = chartReadyRef.current && accIdRef.current?.candlesAndPlotsOnly === true;
      accIdRef.current = accumulator;
      // The full history replacing the quick chart: swap in place, without a
      // loading flash, and keep the view. Any other change starts a fresh chart.
      if (hadChart && active && adapterRef.current && loaded(accumulator)) {
        adapterRef.current.swapStream(accumulator!);
        return;
      }
      setChartReady(false);
      chartReadyRef.current = false;
      if (active && adapterRef.current) {
        adapterRef.current.resetForNewStream();
      } else {
        pendingResetRef.current = true;
      }
    }
  }, [accumulator, active]);

  useEffect(() => {
    if (!active || !pendingResetRef.current || !adapterRef.current) return;
    pendingResetRef.current = false;
    adapterRef.current.resetForNewStream();
  }, [active]);

  // Defer heavy apply so the skeleton can paint first (especially on tab open).
  useEffect(() => {
    const adapter = adapterRef.current;
    if (!active || !engineAllowed || !pageAlive || !adapter || !accumulator) return;

    let cancelled = false;
    const firstPaint = !chartReadyRef.current;
    if (firstPaint) setPainting(true);
    const timer = window.setTimeout(() => {
      if (cancelled || !adapterRef.current) return;
      adapterRef.current.apply(accumulator);
      if (
        accumulator.phase === "live" ||
        accumulator.phase === "ended" ||
        accumulator.phase === "failed"
      ) {
        chartReadyRef.current = true;
        setChartReady(true);
      }
      setPainting(false);
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [accumulator, version, active, engineAllowed, pageAlive]);

  const streamBusy =
    streamStatus === "connecting" || streamStatus === "replaying";
  const waitingForRun =
    !accumulator && (streamStatus === "idle" || streamStatus === "");
  const showSkeleton =
    !waitingForRun &&
    !chartReady &&
    (!engineAllowed ||
      painting ||
      streamBusy ||
      (Boolean(accumulator) &&
        streamStatus !== "error" &&
        streamStatus !== "stale" &&
        streamStatus !== "failed"));

  const statusLabel =
    chartReady && streamStatus === "live"
      ? "Live"
      : chartReady && streamStatus === "ended"
        ? "Ended"
        : streamStatus === "stale"
          ? "Stream interrupted"
          : streamStatus === "failed" || streamStatus === "error"
            ? "Failed"
            : null;

  const waitMessage = waitingForRun
    ? "The chart loads when the backtest finishes."
    : streamBusy || !chartReady || painting
      ? "Loading candles…"
      : null;

  return (
    <div className="relative flex h-full min-h-0 flex-1 flex-col">
      <div
        ref={hostRef}
        className={className}
        style={{ visibility: chartReady && !showSkeleton ? "visible" : "hidden" }}
      />
      {showSkeleton && <ChartSkeleton />}
      {(showSkeleton || waitingForRun) && waitMessage && (
        <div className="pointer-events-none absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 px-6 text-center">
          <div className="rounded-md bg-[var(--bg-card)]/90 px-4 py-3 shadow-sm">
            <div className="text-sm font-medium text-[var(--text-primary)]">{waitMessage}</div>
            {barCount > 0 && showSkeleton && (
              <div className="mt-1 text-xs text-[var(--text-muted)]">
                {barCount.toLocaleString()} bars received…
              </div>
            )}
          </div>
        </div>
      )}
      {!showSkeleton && !waitingForRun && statusLabel && (
        <div className="pointer-events-none absolute bottom-2 right-2 z-10 rounded bg-[var(--bg-card)]/80 px-1.5 py-0.5 text-[10px] text-[var(--text-muted)]">
          {statusLabel}
          {barCount > 0 ? ` · ${barCount.toLocaleString()} bars` : ""}
        </div>
      )}
      {chartReady && !showSkeleton && historyLoading && (
        <div
          role="status"
          className="pointer-events-none absolute left-1/2 top-3 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--bg-card)] px-3 py-1 text-xs text-[var(--text-secondary)] shadow-[var(--shadow-md)]"
        >
          <span className="h-3 w-3 animate-spin rounded-full border-2 border-[var(--border)] border-t-[var(--primary)]" aria-hidden />
          Loading older candles and drawings…
          {historyBarCount > 0 && <span className="num text-[var(--text-muted)]">{historyBarCount.toLocaleString()}</span>}
        </div>
      )}
      {chartReady && !showSkeleton && (
        <button
          type="button"
          className="btn btn-soft btn-sm absolute left-3 top-3 z-10"
          title="Scroll to the latest candle"
          onClick={() => adapterRef.current?.scrollToLatest()}
        >
          Latest
        </button>
      )}
    </div>
  );
}
